import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { FullStory } from '@fullstory/browser';

import { toast } from 'shared/components/Toast/Toast';
import { Button } from 'shared/components/Button/Button';
import { PinGraphic } from 'modules/status/components/PinGraphic/PinGraphic';
import { CheckmarkGraphic } from 'shared/components/icons/CheckmarkGraphic';
import { useUser } from 'hooks/useUser';
import {
  BillingAddressFormModal,
  IFormInputType,
} from 'modules/status/components/BillingAddressFormModal/BillingAddressFormModal';
import { useUpdateUser } from 'api/user';
import useOnboardingStep from 'hooks/useOnboardingStep';
import { OnboardingStep } from 'modules/types/util';
import {
  ActionContainer,
  AddressContainer,
  AddressText,
  BodyText,
  BodyTextContainer,
  ConfirmBillingAddressContainer,
  Title,
} from './ConfirmBillingAddress.styles';

export function ConfirmBillingAddress() {
  const [openModal, setOpenModal] = useState(false);

  const { t } = useTranslation();
  const theme = useTheme();
  const { userResult, refetch, selectedAgreement } = useUser();
  const { goToStep } = useOnboardingStep();

  const { mutate: updateUser } = useUpdateUser({
    onSuccess: () => {
      refetch();
      setOpenModal(false);
      toast({
        type: 'success',
        title: t('toast.success'),
        message: t('toast.addressUpdated'),
        theme,
      });
    },
    onError: () => {
      toast({
        type: 'error',
        title: t('toast.error'),
        message: t('toast.somethingWentWrong'),
        theme,
      });
    },
  });

  const address = userResult?.user?.address;
  const addressLine1 = `${address?.street_1} ${address?.street_2}`;
  const addressLine2 = `${address?.city}, ${address?.state} ${address?.zip_code}`;

  const handleClickConfirm = () => {
    FullStory('trackEvent', {
      name: `Onboarding - Billing Address Confirmation: Clicked ${address?.confirmed ? 'Next' : 'Yes, Confirm'}`,
      properties: {
        page: 'Status',
        onboardingStep: 'Confirm Address',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });

    if (address?.confirmed) {
      goToStep(OnboardingStep.SYSTEM_INSTALLATION);
      return;
    }
    updateUser({
      user_id: userResult?.user?.id!,
      body: { address: { ...address, confirmed: true } },
    });
  };

  const handleSubmitBillingForm = (formData: IFormInputType) => {
    updateUser({
      user_id: userResult?.user?.id!,
      body: { address: { ...formData, confirmed: true } },
    });

    FullStory('trackEvent', {
      name: `Onboarding - New Billing Address: Clicked Update`,
      properties: {
        page: 'Status',
        onboardingStep: 'Confirm Address',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });
  };

  const handleOpenModal = () => {
    setOpenModal(true);

    FullStory('trackEvent', {
      name: `Onboarding - Billing Address Confirmation: Clicked ${address?.confirmed ? 'Update Address' : 'No, Update'}`,
      properties: {
        page: 'Status',
        onboardingStep: 'Confirm Address',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      <ConfirmBillingAddressContainer>
        {address?.confirmed ? <CheckmarkGraphic /> : <PinGraphic />}
        <Title as="h1" $addressConfirmed={!!address?.confirmed}>
          {address?.confirmed
            ? t('onboarding.confirmBillingAddress.confirmed.title')
            : t('onboarding.confirmBillingAddress.unconfirmed.title')}
        </Title>

        <BodyTextContainer>
          <BodyText as="p">
            {address?.confirmed
              ? t('onboarding.confirmBillingAddress.confirmed.body')
              : t('onboarding.confirmBillingAddress.unconfirmed.body')}
          </BodyText>
        </BodyTextContainer>

        <AddressContainer>
          <AddressText as="p">{addressLine1}</AddressText>
          <AddressText as="p">{addressLine2}</AddressText>
        </AddressContainer>

        <ActionContainer>
          <Button
            label={address?.confirmed ? t('cta.next') : t('cta.confirm')}
            isFullWidth
            onClick={handleClickConfirm}
          />
          <Button
            styleVariant="tertiary"
            label={
              address?.confirmed
                ? t('onboarding.confirmBillingAddress.updateAddress')
                : t('cta.noUpdate')
            }
            onClick={handleOpenModal}
          />
        </ActionContainer>
      </ConfirmBillingAddressContainer>
      <BillingAddressFormModal
        openBillingFormModal={openModal}
        handleCloseBillingForm={handleCloseModal}
        handleBackBillingForm={handleCloseModal}
        handleSubmitBillingForm={handleSubmitBillingForm}
      />
    </>
  );
}

export default ConfirmBillingAddress;
